import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Play, Quote } from 'lucide-react';
import { Button } from './ui/button';

// Testimonial videos data - easily expandable for 5-6 videos
const testimonials = [
  {
    id: 1,
    vimeoId: '1188556955',
    vimeoHash: 'c3d4240191',
    name: 'Viewer Testimonial 1',
  },
  {
    id: 2,
    vimeoId: '1173563616',
    vimeoHash: '',
    name: 'Viewer Testimonial 2',
  },
  {
    id: 3,
    vimeoId: '1171419352',
    vimeoHash: '0c73f87ff5',
    name: 'Viewer Testimonial 3',
  },
];

const TestimonialsModal = ({ isOpen, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const modalRef = useRef(null);

  const currentTestimonial = testimonials[currentIndex];
  const hasMultiple = testimonials.length > 1;

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Auto-play when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
      setCurrentIndex(0);
    }
  }, [isOpen]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
    setIsPlaying(true);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
    setIsPlaying(true);
  };

  const handleBackdropClick = (e) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Build Vimeo URL
  // Modern browsers REQUIRE muted=1 for autoplay to work
  // Users can unmute via Vimeo's controls after playback starts
  // playsinline prevents fullscreen on iOS
  const hashParam = currentTestimonial.vimeoHash ? `&h=${currentTestimonial.vimeoHash}` : '';
  const vimeoUrl = `https://player.vimeo.com/video/${currentTestimonial.vimeoId}?autoplay=1&muted=1&title=0&byline=0&portrait=0&playsinline=1${hashParam}`;

  return (
    <div 
      ref={modalRef}
      className="testimonials-modal-overlay"
      onClick={handleBackdropClick}
      data-testid="testimonials-modal"
    >
      <div className="testimonials-modal-container">
        {/* Close Button */}
        <button 
          className="testimonials-close-btn"
          onClick={onClose}
          aria-label="Close testimonials"
          data-testid="testimonials-close-btn"
        >
          <X size={24} />
        </button>

        {/* Video Player */}
        <div className="testimonials-video-wrapper">
          <iframe
            src={vimeoUrl}
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title={`Testimonial from ${currentTestimonial.name}`}
            className="testimonials-video-iframe"
          />
        </div>

        {/* Navigation Controls - Only show if multiple videos */}
        {hasMultiple && (
          <div className="testimonials-nav-container">
            <Button
              onClick={handlePrevious}
              className="testimonials-nav-btn"
              variant="outline"
              data-testid="testimonials-prev-btn"
            >
              <ChevronLeft size={20} />
              <span>Previous</span>
            </Button>
            
            <div className="testimonials-indicator">
              <span className="testimonials-counter">
                {currentIndex + 1} / {testimonials.length}
              </span>
            </div>

            <Button
              onClick={handleNext}
              className="testimonials-nav-btn"
              variant="outline"
              data-testid="testimonials-next-btn"
            >
              <span>Next</span>
              <ChevronRight size={20} />
            </Button>
          </div>
        )}

        {/* Single video message */}
        {!hasMultiple && (
          <div className="testimonials-single-message">
            <p>More testimonials coming soon...</p>
          </div>
        )}
      </div>
    </div>
  );
};

// CTA Box Component to trigger the modal
export const TestimonialsCTA = ({ onClick, backgroundImage }) => {
  return (
    <div className="testimonials-cta-section" data-testid="testimonials-cta">
      <button 
        className="testimonials-cta-box"
        onClick={onClick}
        data-testid="testimonials-cta-btn"
        style={backgroundImage ? {
          backgroundImage: `linear-gradient(rgba(10, 10, 26, 0.75), rgba(10, 10, 26, 0.85)), url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        } : {}}
      >
        <div className="testimonials-cta-content">
          <div className="testimonials-cta-icon">
            <Quote size={32} />
          </div>
          <div className="testimonials-cta-text">
            <h3>What People Are Saying After Watching</h3>
            <p>Click to watch testimonials from our community</p>
          </div>
          <div className="testimonials-cta-play">
            <Play size={28} fill="currentColor" />
          </div>
        </div>
        <div className="testimonials-cta-glow"></div>
      </button>
    </div>
  );
};

export default TestimonialsModal;
